/**
 * App.jsx
 * @version 2.2.1 — + fetchSlots au login (usePhotoSlotsStore)
 */

import React, { useEffect, useState } from 'react'
import { Toaster }              from 'react-hot-toast'
import { useAppStore }          from './store/useAppStore'
import { usePhotoSlotsStore }   from './store/usePhotoSlotsStore'
import { useChecklistStore }  from './store/useChecklistStore'
import { useDamagesStore }    from './store/useDamagesStore'
import { useVersionReload }     from './hooks/useVersionReload'
import { useUploadWorker }      from './services/useUploadWorker'
import { APP_VERSION }          from './version'
import { useSettingsStore }     from './store/useSettingsStore'
import ErrorBoundary            from './components/ErrorBoundary'
import { pushHistoryStep, initHistoryListener } from './utils/history'

import HomeScreen               from './steps/HomeScreen'
import LoginScreen              from './steps/LoginScreen'
import SyncScreen               from './steps/SyncScreen'
import ContractListScreen       from './steps/ContractListScreen'
import ChooseScreen             from './steps/ChooseScreen'
import RecapScreen              from './steps/RecapScreen'
import PhotoScreen              from './steps/PhotoScreen'
import FuelScreen               from './steps/FuelScreen'
import SecurityCheckScreen      from './steps/SecurityCheckScreen'
import SignatureScreen          from './steps/SignatureScreen'
import DoneScreen               from './steps/DoneScreen'
import AdminScreen              from './steps/AdminScreen'
import PendingQueueScreen       from './steps/PendingQueueScreen'
import AdminStatsScreen         from './steps/AdminStatsScreen'
import SelectionScreen          from './steps/SelectionScreen'
import PrepInspectScreen        from './steps/PrepInspectScreen'
import PrepLinkScreen           from './steps/PrepLinkScreen'
import PendingMrcScreen         from './steps/PendingMrcScreen'

const SCREENS = {
  home:       HomeScreen,
  login:      LoginScreen,
  sync:       SyncScreen,
  contracts:  ContractListScreen,
  choose:     ChooseScreen,
  recap:      RecapScreen,
  photos:     PhotoScreen,
  fuel:       FuelScreen,
  security:   SecurityCheckScreen,
  signatures: SignatureScreen,
  done:       DoneScreen,
  admin:      AdminScreen,
  queue:      PendingQueueScreen,
  stats:      AdminStatsScreen,
  selection:  SelectionScreen,
  'prep-inspect': PrepInspectScreen,
  'prep-link':    PrepLinkScreen,
  'pending-mrc':  PendingMrcScreen,
}

// ── Worker upload — silencieux, zéro UI ──────────────────────────────────────
function AppWorkers() {
  useUploadWorker()
  return null
}

export default function App() {
  useVersionReload()

  const { step, isAuthenticated, credentials, setStep } = useAppStore()
  const fetchSlots = usePhotoSlotsStore(s => s.fetchSlots)   // ✅ AJOUT
  const fetchChecklistItems = useChecklistStore(s => s.fetchItems)
  const fetchDamages = useDamagesStore(s => s.fetchDamages)   // ✅ AJOUT
  const [hydrated, setHydrated] = useState(false)

  // ── Hydration Zustand persist ─────────────────────────────────────────────
  useEffect(() => {
    const unsub = useAppStore.persist.onFinishHydration(() => setHydrated(true))
    if (useAppStore.persist.hasHydrated()) setHydrated(true)
    return unsub
  }, [])

  // ✅ fetchSlots dès que l'agent est authentifié (après hydration)
  // Se re-déclenche si credentials changent (reconnexion, changement de compte)
  useEffect(() => {
    if (hydrated && isAuthenticated && credentials) {
      fetchSlots()
      fetchChecklistItems()
      fetchDamages()
    }
  }, [hydrated, isAuthenticated, credentials])   // ✅ AJOUT

  // ── Synchronisation Thème (Dark Mode & Glassmorphism) ───────────────────
  const theme_mode = useSettingsStore(s => s.settings.theme_mode)
  const theme_glass = useSettingsStore(s => s.settings.theme_glass)
  const theme_color = useSettingsStore(s => s.settings.theme_color) // ✓ Added color extraction
  
  useEffect(() => {
    const isDark = theme_mode === 'dark' || (theme_mode === 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches)
    
    // Bascule de la classe 'dark' sur <html>
    if (isDark) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
    
    // Application Couleur et Glass
    document.documentElement.dataset.theme = theme_color || 'indigo'
    if (theme_glass) {
      document.documentElement.dataset.premium = 'glass'
    } else {
      delete document.documentElement.dataset.premium
    }
  }, [theme_mode, theme_glass, theme_color])

  // ── Support Bouton Retour Android (History API) ───────────────────────────
  useEffect(() => {
    if (!hydrated) return

    // Écouter le bouton retour matériel/navigateur
    const cleanup = initHistoryListener((prevStep) => {
      // Sécurité : éviter de revenir sur login si déjà authentifié
      if (prevStep === 'login' && isAuthenticated) {
        setStep('home')
        return
      }
      // On met à jour le store sans repousser dans l'historique (déjà fait par le navigateur)
      setStep(prevStep)
    })

    return cleanup
  }, [hydrated, isAuthenticated, setStep])

  // Synchroniser le store vers l'historique
  useEffect(() => {
    if (hydrated && step) {
      pushHistoryStep(step)
    }
  }, [hydrated, step])

  if (!hydrated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-4 border-blue-500 border-t-transparent animate-spin"/>
      </div>
    )
  }

  const resolvedStep = isAuthenticated ? (step || 'home') : 'login'
  const Screen       = SCREENS[resolvedStep] || LoginScreen

  const screenProps = resolvedStep === 'admin'
    ? { onBack: () => setStep('sync') }
    : {}

  return (
    <ErrorBoundary>
      <Toaster position="top-center" toastOptions={{ duration: 3000 }}/>

      {isAuthenticated && <AppWorkers />}

      <div className="fixed bottom-1 right-1 z-50 text-[9px] text-gray-400 bg-black/20 px-1.5 py-0.5 rounded-full pointer-events-none select-none">
        v{APP_VERSION}
      </div>

      <div className="animate-fadeIn min-h-screen">
        <Screen {...screenProps} />
      </div>
    </ErrorBoundary>
  )
}